/**
 * FilterBar - Reusable filter component for all views
 * Provides: Search, Apply button, Filter dropdowns, Active chips, Sort, Custom actions
 */

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterConfig {
  id: string;
  icon: string;
  label: string;
  type: 'checkbox' | 'tags' | 'radio';
  options: FilterOption[];
  multiple: boolean;
}

export interface ActionButton {
  id: string;
  icon: string;
  title: string;
  danger?: boolean;
}

export interface FilterBarConfig {
  showSearch: boolean;
  showApplyButton: boolean;
  filters: FilterConfig[];
  showSort: boolean;
  customActions?: ActionButton[];
  viewContext: string;
  title?: string;
  titleBadge?: { count: number; label: string };
}

export interface FilterState {
  search: string;
  activeFilters: Map<string, Set<string>>;
  sortField: string;
  sortDirection: 'asc' | 'desc';
}

export type FilterChangeCallback = (state: FilterState) => void;
export type ActionCallback = (actionId: string) => void;

export class FilterBar {
  private container: HTMLElement;
  private config: FilterBarConfig;
  private state: FilterState;

  private onFilterChange: FilterChangeCallback | null = null;
  private onApply: FilterChangeCallback | null = null;
  private onAction: ActionCallback | null = null;

  // DOM references
  private searchInput: HTMLInputElement | null = null;
  private chipsContainer: HTMLElement | null = null;

  constructor(containerId: string, config: FilterBarConfig) {
    const container = document.getElementById(containerId);
    if (!container) {
      throw new Error(`FilterBar container not found: ${containerId}`);
    }
    this.container = container;
    this.config = config;
    this.state = {
      search: '',
      activeFilters: new Map(),
      sortField: 'updated_at',
      sortDirection: 'desc'
    };

    // Initialize filter sets
    for (const filter of config.filters) {
      this.state.activeFilters.set(filter.id, new Set());
    }

    this.render();
    this.setupEventListeners();
  }

  private render(): void {
    const { config } = this;

    this.container.innerHTML = `
      <div class="filter-bar" data-view="${config.viewContext}">
        <div class="filter-bar-left">
          ${config.title ? `
            <h1 class="filter-bar-title">${config.title}</h1>
            ${config.titleBadge ? `<span class="filter-bar-badge" id="filter-bar-badge-${config.viewContext}">${config.titleBadge.count} ${config.titleBadge.label}</span>` : ''}
          ` : ''}

          ${config.showSearch ? `
            <div class="filter-bar-search">
              <span class="material-symbols-outlined">search</span>
              <input type="text" id="filter-search-${config.viewContext}" placeholder="Find...">
            </div>
          ` : ''}

          ${config.showApplyButton ? `
            <button class="filter-bar-apply" id="filter-apply-${config.viewContext}" title="Apply Filters">
              <span class="material-symbols-outlined">bolt</span>
            </button>
          ` : ''}

          ${config.filters.length > 0 ? `
            <div class="filter-bar-filters">
              ${config.filters.map(f => this.renderFilterDropdown(f)).join('')}
            </div>
          ` : ''}

          <div class="filter-bar-chips" id="filter-chips-${config.viewContext}"></div>
        </div>

        <div class="filter-bar-right">
          ${config.showSort ? `
            <button class="filter-bar-action" id="filter-sort-${config.viewContext}" title="Sort">
              <span class="material-symbols-outlined">sort</span>
            </button>
          ` : ''}

          ${config.customActions && config.customActions.length > 0 ? `
            <div class="filter-bar-divider"></div>
            ${config.customActions.map(a => `
              <button class="filter-bar-action ${a.danger ? 'danger' : ''}" id="filter-action-${a.id}-${config.viewContext}" title="${a.title}">
                <span class="material-symbols-outlined">${a.icon}</span>
              </button>
            `).join('')}
          ` : ''}
        </div>
      </div>
    `;

    // Store references
    this.searchInput = document.getElementById(`filter-search-${config.viewContext}`) as HTMLInputElement;
    this.chipsContainer = document.getElementById(`filter-chips-${config.viewContext}`);
  }

  private renderFilterDropdown(filter: FilterConfig): string {
    const { viewContext } = this.config;

    return `
      <div class="filter-dropdown" data-filter="${filter.id}">
        <button class="filter-dropdown-btn" id="filter-btn-${filter.id}-${viewContext}">
          <span class="material-symbols-outlined">${filter.icon}</span>
          <span class="filter-dropdown-label">${filter.label}</span>
          <span class="material-symbols-outlined filter-dropdown-arrow">expand_more</span>
        </button>
        <div class="filter-dropdown-menu hidden" id="filter-menu-${filter.id}-${viewContext}">
          ${filter.type === 'tags' ? `
            <div class="filter-tags-search">
              <input type="text" id="filter-tags-search-${filter.id}-${viewContext}" placeholder="Search ${filter.label.toLowerCase()}...">
            </div>
          ` : ''}
          <div class="filter-dropdown-options" id="filter-options-${filter.id}-${viewContext}">
            ${filter.options.map(opt => `
              <label class="filter-option">
                <input type="checkbox" value="${opt.value}" data-filter="${filter.id}">
                <span>${filter.type === 'tags' ? '#' : ''}${opt.label}</span>
              </label>
            `).join('')}
          </div>
        </div>
      </div>
    `;
  }

  private setupEventListeners(): void {
    const { viewContext } = this.config;

    // Search input with debounce
    if (this.searchInput) {
      let searchTimeout: ReturnType<typeof setTimeout>;
      this.searchInput.addEventListener('input', () => {
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(() => {
          this.state.search = this.searchInput?.value.trim().toLowerCase() || '';
          this.notifyFilterChange();
        }, 300);
      });
    }

    // Apply button
    document.getElementById(`filter-apply-${viewContext}`)?.addEventListener('click', () => {
      if (this.onApply) {
        this.onApply(this.getState());
      }
    });

    // Sort button
    document.getElementById(`filter-sort-${viewContext}`)?.addEventListener('click', (e) => {
      this.showSortMenu(e);
    });

    // Filter dropdowns
    for (const filter of this.config.filters) {
      this.setupFilterDropdown(filter);
    }

    // Custom action buttons
    if (this.config.customActions) {
      for (const action of this.config.customActions) {
        document.getElementById(`filter-action-${action.id}-${viewContext}`)?.addEventListener('click', () => {
          if (this.onAction) {
            this.onAction(action.id);
          }
        });
      }
    }

    // Close dropdowns on outside click
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.filter-dropdown')) {
        this.container.querySelectorAll('.filter-dropdown-menu').forEach(menu => {
          menu.classList.add('hidden');
        });
      }
    });
  }

  private setupFilterDropdown(filter: FilterConfig): void {
    const { viewContext } = this.config;
    const btn = document.getElementById(`filter-btn-${filter.id}-${viewContext}`);
    const menu = document.getElementById(`filter-menu-${filter.id}-${viewContext}`);

    // Toggle dropdown
    btn?.addEventListener('click', (e) => {
      e.stopPropagation();
      // Close other menus
      this.container.querySelectorAll('.filter-dropdown-menu').forEach(m => {
        if (m !== menu) m.classList.add('hidden');
      });
      menu?.classList.toggle('hidden');
    });

    // Checkbox handlers
    const optionsContainer = document.getElementById(`filter-options-${filter.id}-${viewContext}`);
    optionsContainer?.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        const value = (checkbox as HTMLInputElement).value;
        const checked = (checkbox as HTMLInputElement).checked;
        const filterSet = this.state.activeFilters.get(filter.id);

        if (filterSet) {
          if (checked) {
            filterSet.add(value);
          } else {
            filterSet.delete(value);
          }
        }

        this.renderChips();
      });
    });

    // Tags search (if applicable)
    if (filter.type === 'tags') {
      const searchInput = document.getElementById(`filter-tags-search-${filter.id}-${viewContext}`) as HTMLInputElement;
      searchInput?.addEventListener('input', () => {
        this.filterTagsOptions(filter.id, searchInput.value);
      });
    }
  }

  private filterTagsOptions(filterId: string, query: string): void {
    const { viewContext } = this.config;
    const optionsContainer = document.getElementById(`filter-options-${filterId}-${viewContext}`);
    const lowerQuery = query.toLowerCase();

    optionsContainer?.querySelectorAll('.filter-option').forEach(option => {
      const label = option.querySelector('span')?.textContent?.toLowerCase() || '';
      (option as HTMLElement).style.display = label.includes(lowerQuery) ? '' : 'none';
    });
  }

  private renderChips(): void {
    if (!this.chipsContainer) return;

    const chips: string[] = [];

    this.state.activeFilters.forEach((values, filterId) => {
      const filter = this.config.filters.find(f => f.id === filterId);
      values.forEach(value => {
        const option = filter?.options.find(o => o.value === value);
        const label = option?.label || value;
        chips.push(`
          <span class="filter-chip" data-filter="${filterId}" data-value="${value}">
            ${filter?.type === 'tags' ? '#' : ''}${label}
            <button class="filter-chip-remove">
              <span class="material-symbols-outlined">close</span>
            </button>
          </span>
        `);
      });
    });

    this.chipsContainer.innerHTML = chips.join('');

    // Chip remove handlers
    this.chipsContainer.querySelectorAll('.filter-chip-remove').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const chip = (btn as HTMLElement).closest('.filter-chip') as HTMLElement;
        const filterId = chip.dataset.filter!;
        const value = chip.dataset.value!;

        this.state.activeFilters.get(filterId)?.delete(value);

        // Uncheck corresponding checkbox
        const checkbox = this.container.querySelector(
          `input[type="checkbox"][value="${value}"][data-filter="${filterId}"]`
        ) as HTMLInputElement;
        if (checkbox) checkbox.checked = false;

        this.renderChips();
        // Auto-apply on chip removal for better UX
        if (this.onApply) {
          this.onApply(this.getState());
        }
      });
    });
  }

  private showSortMenu(e: MouseEvent): void {
    // Remove existing menu
    document.querySelector('.filter-sort-menu')?.remove();

    const btn = e.currentTarget as HTMLElement;
    const rect = btn.getBoundingClientRect();

    const menu = document.createElement('div');
    menu.className = 'filter-sort-menu';
    menu.style.top = `${rect.bottom + 4}px`;
    menu.style.right = `${window.innerWidth - rect.right}px`;

    const options = [
      { field: 'updated_at', label: 'Last Modified' },
      { field: 'created_at', label: 'Date Created' },
      { field: 'title', label: 'Title' },
      { field: 'views', label: 'Views' },
      { field: 'likes', label: 'Likes' },
      { field: 'duration', label: 'Duration' }
    ];

    menu.innerHTML = options.map(opt => `
      <button class="filter-sort-option ${this.state.sortField === opt.field ? 'active' : ''}" data-field="${opt.field}">
        ${opt.label}
        ${this.state.sortField === opt.field ? `
          <span class="material-symbols-outlined">${this.state.sortDirection === 'desc' ? 'arrow_downward' : 'arrow_upward'}</span>
        ` : ''}
      </button>
    `).join('');

    menu.querySelectorAll('.filter-sort-option').forEach(optBtn => {
      optBtn.addEventListener('click', () => {
        const field = (optBtn as HTMLElement).dataset.field!;
        if (this.state.sortField === field) {
          this.state.sortDirection = this.state.sortDirection === 'desc' ? 'asc' : 'desc';
        } else {
          this.state.sortField = field;
          this.state.sortDirection = 'desc';
        }
        menu.remove();
        if (this.onApply) {
          this.onApply(this.getState());
        }
      });
    });

    document.body.appendChild(menu);

    // Close on outside click
    setTimeout(() => {
      const closeHandler = (ev: MouseEvent) => {
        if (!menu.contains(ev.target as Node)) {
          menu.remove();
          document.removeEventListener('click', closeHandler);
        }
      };
      document.addEventListener('click', closeHandler);
    }, 0);
  }

  private notifyFilterChange(): void {
    if (this.onFilterChange) {
      this.onFilterChange(this.getState());
    }
  }

  // Public API

  public getState(): FilterState {
    return {
      search: this.state.search,
      activeFilters: new Map(this.state.activeFilters),
      sortField: this.state.sortField,
      sortDirection: this.state.sortDirection
    };
  }

  public setFilterOptions(filterId: string, options: FilterOption[]): void {
    const { viewContext } = this.config;
    const filter = this.config.filters.find(f => f.id === filterId);
    if (!filter) return;

    filter.options = options;
    const optionsContainer = document.getElementById(`filter-options-${filterId}-${viewContext}`);
    if (!optionsContainer) return;

    const currentSelected = this.state.activeFilters.get(filterId) || new Set();

    optionsContainer.innerHTML = options.map(opt => `
      <label class="filter-option">
        <input type="checkbox" value="${opt.value}" data-filter="${filterId}" ${currentSelected.has(opt.value) ? 'checked' : ''}>
        <span>${filter.type === 'tags' ? '#' : ''}${opt.label}</span>
      </label>
    `).join('');

    // Re-attach handlers
    optionsContainer.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        const value = (checkbox as HTMLInputElement).value;
        const checked = (checkbox as HTMLInputElement).checked;
        const filterSet = this.state.activeFilters.get(filterId);

        if (filterSet) {
          if (checked) {
            filterSet.add(value);
          } else {
            filterSet.delete(value);
          }
        }

        this.renderChips();
      });
    });
  }

  public setActiveFilter(filterId: string, values: string[]): void {
    // Set the active values for a filter
    this.state.activeFilters.set(filterId, new Set(values));

    // Update checkboxes in the UI
    const { viewContext } = this.config;
    const optionsContainer = document.getElementById(`filter-options-${filterId}-${viewContext}`);
    if (optionsContainer) {
      optionsContainer.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
        const input = checkbox as HTMLInputElement;
        input.checked = values.includes(input.value);
      });
    }

    this.renderChips();
  }

  public updateBadge(count: number, label?: string): void {
    const badge = document.getElementById(`filter-bar-badge-${this.config.viewContext}`);
    if (badge) {
      badge.textContent = label ? `${count} ${label}` : `${count}`;
    }
  }

  public clearAll(): void {
    this.state.search = '';
    if (this.searchInput) this.searchInput.value = '';

    this.state.activeFilters.forEach(set => set.clear());

    // Uncheck all checkboxes
    this.container.querySelectorAll('input[type="checkbox"]').forEach(cb => {
      (cb as HTMLInputElement).checked = false;
    });

    this.renderChips();
  }

  // Callbacks
  public onFilterChangeCallback(callback: FilterChangeCallback): void {
    this.onFilterChange = callback;
  }

  public onApplyCallback(callback: FilterChangeCallback): void {
    this.onApply = callback;
  }

  public onActionCallback(callback: ActionCallback): void {
    this.onAction = callback;
  }
}
